import type { AppError } from '@aibi/shared';
import { cn } from '@aibi/ui';
import { CircleAlert, Info, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { hintOf } from '../../api/http-client';

/** Error del servidor con la pista accionable (`details[path='hint']`) si la hay. */
export function ErrorNotice({
  error,
  className,
}: {
  readonly error: AppError;
  readonly className?: string;
}) {
  const hint = hintOf(error);
  return (
    <Notice tone="error" className={className}>
      <p>{error.message}</p>
      {hint && <p className="mt-1 text-foreground/80">{hint}</p>}
    </Notice>
  );
}

const TONES = {
  error: { icon: CircleAlert, classes: 'border-destructive/30 bg-destructive/5 text-destructive' },
  warning: { icon: TriangleAlert, classes: 'border-warning/30 bg-warning/10 text-foreground' },
  info: { icon: Info, classes: 'border-border bg-muted/50 text-foreground' },
} as const;

export function Notice({
  tone,
  children,
  className,
}: {
  readonly tone: keyof typeof TONES;
  readonly children: ReactNode;
  readonly className?: string | undefined;
}) {
  const { icon: Icon, classes } = TONES[tone];
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn('flex gap-2 rounded-md border p-2.5 text-xs', classes, className)}
    >
      <Icon aria-hidden className="mt-0.5 size-3.5 shrink-0" />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
